import type { AreaId, PersonId } from '@shared/brand';

/**
 * Lo que cada persona confirma —o corrige— la primera vez que entra.
 *
 * El padrón entró de un formulario de Google contestado por 338 personas, y su
 * calidad se conoce: dos fichas traen «Servicios profesionales» donde va la fecha
 * de ingreso, el organigrama se dedujo de un campo de texto libre con 99 grafías
 * para unos cuarenta jefes, y 38 personas no tienen quien les firme. Revisar 338
 * fichas desde RR.HH. es un trabajo que no termina nunca; cada quien revisa la
 * suya en veinte segundos y sabe la respuesta sin buscarla.
 *
 * **Se guarda lo que se le enseñó, no sólo lo que corrigió.** `shown` congela el
 * valor que la persona tenía delante al decir que estaba mal. Sin eso, RR.HH. lee
 * «la fecha está mal» tres semanas después, mira la ficha, ve una fecha que ya
 * cambió por otro camino y no puede saber si el reporte sigue vivo.
 *
 * **Un documento por persona, con su id.** Así la confirmación es idempotente —dos
 * clics no son dos documentos— y «¿ya contestó?» es leer un documento y no una
 * consulta.
 */
export interface DataConfirmation {
  /** Mismo valor que el id del documento. */
  readonly personId: PersonId;
  /**
   * El área en el momento de confirmar.
   *
   * Desnormalizada porque es lo que las reglas comparan contra el alcance de
   * quien lee: sin ella, el administrador de un área no puede pedir los reportes
   * de su gente sin poder pedir también los de las otras siete.
   */
  readonly areaId: AreaId;
  /** Para que la bandeja de RR.HH. se lea sin resolver 338 fichas. */
  readonly personName: string;
  readonly confirmedAt: Date;
  /**
   * Vacío significa «todo estaba correcto», y es un dato tan bueno como el otro:
   * distingue a quien revisó y aprobó de quien todavía no ha entrado.
   */
  readonly corrections: readonly DataCorrection[];
  /** Cuándo RR.HH. lo dio por atendido. `null` mientras siga en la bandeja. */
  readonly resolvedAt: Date | null;
  readonly resolvedByPersonId: PersonId | null;
  /**
   * Qué ronda contestó. Ver `CONFIRMATION_VERSION`.
   *
   * Los 38 documentos de agosto no traen la clave, así que el decodificador la
   * lee como **1**: es lo que eran, no un valor por omisión elegido a la ligera.
   * Leerla como la versión actual sería peor que no tenerla — daría por
   * contestado el cuestionario nuevo a quien sólo vio el viejo.
   */
  readonly version: number;
}

export interface DataCorrection {
  readonly field: ConfirmableField;
  /** Lo que el portal le enseñó, tal cual, en el momento de reportar. */
  readonly shown: string;
  /** Lo que la persona dice que debería decir. */
  readonly correct: string;
}

/**
 * Qué se pone a confirmar, y en el orden en que se pregunta.
 *
 * Son los campos que la carga masiva pudo equivocar y que la persona conoce sin
 * consultar a nadie. Queda fuera todo lo que ella no puede juzgar mejor que el
 * portal —el rol, los permisos, el estado de la cuenta— porque preguntarlo sólo
 * produce respuestas inventadas.
 */
export const CONFIRMABLE_FIELDS = [
  'nombre',
  'correo',
  'nacimiento',
  'fechaIngreso',
  'area',
  'subArea',
  'puesto',
  'lider',
  'saldo',
  'talla',
  'papaMama',
] as const;

export type ConfirmableField = (typeof CONFIRMABLE_FIELDS)[number];

/**
 * Lo que la tarjeta pone a **confirmar**, que ya no es todo el catálogo.
 *
 * `papaMama` sigue en `CONFIRMABLE_FIELDS` y tiene que seguir: en producción hay
 * una corrección guardada con esa llave, y sacarla del catálogo haría que
 * `isConfirmableField` la rechazara — el reporte desaparecería de la bandeja sin
 * error y sin log. Lo que cambia es que **ya no se ofrece como fila que
 * reportar**, porque ahora se pregunta directo.
 *
 * Y se pregunta directo por lo que pasó: alguien leyó «Papá o mamá — Sin
 * registrar» y contestó con el nombre de su madre. Un rótulo que hay que
 * interpretar produce respuestas a otra pregunta, y aquí ni el tipo ni ninguna
 * prueba podían verlo — sólo leer lo que la gente contestó.
 */
export const REPORTABLE_FIELDS = CONFIRMABLE_FIELDS.filter(
  (field): field is Exclude<ConfirmableField, 'papaMama'> => field !== 'papaMama',
);

/**
 * Qué ronda de preguntas es ésta. Se sube cuando el cuestionario cambia.
 *
 * **1** fue el cuestionario de agosto: ocho campos, todos de trabajo. La
 * contestaron 38 personas.
 *
 * **2** añade los tres que llegaron con el libro de eventos —nacimiento, talla y
 * si es papá o mamá—, que ninguna de esas 38 vio. Sin versión, «¿ya contestó?»
 * era «¿existe el documento?», y esas 38 no volverían a ver la tarjeta nunca: la
 * puerta las daría por hechas para siempre sobre un cuestionario que ya no
 * existe.
 *
 * **Y no se borran los documentos para volver a preguntar**, que era la otra
 * salida y la más tentadora. Once de esos 38 traen correcciones que RR.HH.
 * todavía no ha atendido, y borrarlos vaciaría «Datos reportados» sin que nadie
 * lo notara. Una versión pregunta de nuevo **y** conserva lo dicho.
 */
export const CONFIRMATION_VERSION = 3;

/**
 * Si hay que volver a preguntarle a esta persona.
 *
 * `null` es quien nunca contestó. Una versión vieja es quien contestó otro
 * cuestionario — y las dos cosas se atienden igual: enseñando la tarjeta.
 *
 * Vive aquí y no en la pantalla porque es la regla, no el render: la puerta, el
 * servidor y cualquier informe que cuente «cuántos faltan» tienen que estar de
 * acuerdo, y tres copias de un `<` es como se acaba con tres respuestas.
 */
export const needsConfirmation = (confirmation: DataConfirmation | null): boolean =>
  confirmation === null || confirmation.version < CONFIRMATION_VERSION;

/**
 * Cómo se nombra cada campo a quien lo lee.
 *
 * «Jefe directo» y no «líder»: es la palabra con la que se preguntó en el
 * formulario del que salió el organigrama, y quien contesta debe reconocer lo que
 * ya contestó una vez.
 */
export const FIELD_LABEL: Record<ConfirmableField, string> = {
  nombre: 'Nombre completo',
  correo: 'Correo corporativo',
  nacimiento: 'Fecha de nacimiento',
  fechaIngreso: 'Fecha de ingreso',
  area: 'Área',
  subArea: 'Sub área',
  puesto: 'Puesto',
  lider: 'Jefe directo',
  saldo: 'Días disponibles',
  talla: 'Talla de playera',
  papaMama: '¿Eres papá o mamá?',
};

/**
 * El tope de lo que se escribe en una corrección.
 *
 * Da para una fecha, un nombre o una frase que explique el matiz —«ingresé en
 * marzo de 2021, no en agosto de 2026»— y no para un relato. Lo que no cabe aquí
 * es una conversación, y esa se tiene por otro lado.
 */
export const MAX_CORRECTION_LENGTH = 300;

export const isConfirmableField = (value: unknown): value is ConfirmableField =>
  typeof value === 'string' && (CONFIRMABLE_FIELDS as readonly string[]).includes(value);

/** Si trajo correcciones, alguien tiene que leerlas. */
export const wasReported = (confirmation: DataConfirmation): boolean =>
  confirmation.corrections.length > 0;

/** Lo que sigue esperando en la bandeja de RR.HH. */
export const isPending = (confirmation: DataConfirmation): boolean =>
  wasReported(confirmation) && confirmation.resolvedAt === null;

/**
 * Deja una corrección lista para guardar, o dice que no sirve.
 *
 * **Marcar un campo no es reportarlo.** Sin el valor correcto, RR.HH. recibe «la
 * fecha de ingreso está mal» y tiene que escribirle a la persona para saber cuál
 * es — o sea, el reporte no ahorra el viaje que venía a ahorrar. Una casilla
 * marcada y vacía se descarta en silencio en vez de convertirse en una tarea que
 * nadie puede cerrar.
 *
 * `shown` sí puede ir vacío: «Sin registrar» es exactamente lo que alguien va a
 * querer corregir, y es el hueco lo que se está señalando.
 */
export const cleanCorrection = (input: {
  readonly field: ConfirmableField;
  readonly shown: string;
  readonly correct: string;
}): DataCorrection | null => {
  const correct = input.correct.trim().slice(0, MAX_CORRECTION_LENGTH);
  if (correct.length === 0) return null;
  return {
    field: input.field,
    shown: input.shown.trim().slice(0, MAX_CORRECTION_LENGTH),
    correct,
  };
};

/**
 * Las correcciones que de verdad se mandan, en el orden del cuestionario.
 *
 * Ordenarlas aquí y no al pintarlas es lo que hace que dos reportes del mismo
 * campo se lean igual en la bandeja, vengan del orden en que cada quien marcó las
 * casillas.
 */
export const cleanCorrections = (
  inputs: readonly {
    readonly field: ConfirmableField;
    readonly shown: string;
    readonly correct: string;
  }[],
): readonly DataCorrection[] => {
  const byField = new Map<ConfirmableField, DataCorrection>();
  for (const input of inputs) {
    const clean = cleanCorrection(input);
    /* La última gana: si la pantalla mandara el mismo campo dos veces, guardar
       las dos dejaría a RR.HH. eligiendo cuál de dos verdades es la buena. */
    if (clean !== null) byField.set(clean.field, clean);
  }
  return CONFIRMABLE_FIELDS.map((field) => byField.get(field)).filter(
    (correction): correction is DataCorrection => correction !== undefined,
  );
};
